import React, { useState, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET, CLOUDINARY_PORTFOLIO_FOLDER } from '../config';

const PortfolioForm = ({ categories, onSave, submitting }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    long_description: '',
    category: '',
    type: '',
    materials: '',
    gallery: [],
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState(false);



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Verificar tipos de arquivo
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/avif'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      alert('Apenas arquivos PNG, JPG, JPEG, WebP e AVIF são permitidos.');
      return;
    }

    // Limitar a 10 imagens
    const totalImages = formData.gallery.length + imageFiles.length + files.length;
    if (totalImages > 10) {
      alert('Máximo de 10 imagens permitidas por projeto');
      return;
    }

    setImageFiles(prev => [...prev, ...files]);
  };

  const removeImageFile = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  const uploadImageToCloudinary = async (file, index) => {
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    if (CLOUDINARY_PORTFOLIO_FOLDER) uploadFormData.append('folder', CLOUDINARY_PORTFOLIO_FOLDER);
    
    // Gerar public_id único
    const timestamp = Date.now();
    const cleanTitle = (formData.title || 'portfolio').toLowerCase().replace(/[^a-z0-9-]+/g, '-');
    uploadFormData.append('public_id', `${cleanTitle}-${timestamp}-${index}`);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: uploadFormData
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data?.error?.message || 'Erro no upload da imagem');
      }

      return data.secure_url || data.url;
    } catch (error) {
      console.error('Erro no upload para Cloudinary:', error);
      throw error;
    }
  };

  const uploadImages = async () => {
    if (imageFiles.length === 0) return [];

    setUploading(true);
    const uploadedUrls = [];

    try {
      // Validar se Cloudinary está configurado
      if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
        throw new Error('Cloudinary não está configurado. Configure REACT_APP_CLOUDINARY_CLOUD_NAME e REACT_APP_CLOUDINARY_UPLOAD_PRESET.');
      }

      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        
        // Simular progresso
        setUploadProgress(prev => ({
          ...prev,
          [i]: 20
        }));

        const imageUrl = await uploadImageToCloudinary(file, i);
        uploadedUrls.push(imageUrl);

        // Atualizar progresso
        setUploadProgress(prev => ({
          ...prev,
          [i]: 100
        }));
      }
    } finally {
      setUploading(false);
      setUploadProgress({});
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Título é obrigatório');
      return;
    }

    if (!formData.description.trim()) {
      alert('Descrição é obrigatória');
      return;
    }

    if (!formData.category) {
      alert('Categoria é obrigatória');
      return;
    }

    if (!formData.type.trim()) {
      alert('Tipo de material é obrigatório');
      return;
    }

    if (formData.gallery.length === 0 && imageFiles.length === 0) {
      alert('Pelo menos uma imagem é obrigatória');
      return;
    }

    try {
      const newImageUrls = await uploadImages();
      const allImages = [...formData.gallery, ...newImageUrls];

      await onSave({
        ...formData,
        gallery: allImages,
      });

      // Resetar formulário após salvar
      setFormData({
        title: '',
        description: '',
        long_description: '',
        category: '',
        type: '',
        materials: '',
        client: '',
        location: '',
        year: new Date().getFullYear(),
        gallery: [],
      });
      setImageFiles([]);
    } catch (error) {
      console.error('Erro ao salvar projeto:', error);
      alert(`Erro ao salvar projeto: ${error.message}`);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Título do Projeto *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
            placeholder="Ex: Sala de Estar Aconchegante"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categoria *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
              required
            >
              <option value="">Selecione uma categoria</option>
              <option value="Residencial">Residencial</option>
              <option value="Comercial">Comercial</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Material *
            </label>
            <input
              type="text"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
              placeholder="Ex: Piso Vinílico, Piso Laminado, Carpete"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descrição Resumida *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
            placeholder="Descrição curta que aparece no card do projeto..."
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descrição Detalhada
          </label>
          <textarea
            name="long_description"
            value={formData.long_description}
            onChange={handleInputChange}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
            placeholder="Descrição completa que aparece no modal do projeto..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Materiais Utilizados
          </label>
          <input
            type="text"
            name="materials"
            value={formData.materials}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
            placeholder="Ex: Piso Vinílico LVT Eucafloor"
          />
        </div>



        {/* Imagens Existentes */}
        {formData.gallery.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Galeria Atual
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {formData.gallery.map((imageUrl, index) => (
                <div key={index} className="relative group">
                  <img
                    src={imageUrl}
                    alt={`Projeto ${index + 1}`}
                    className="w-full h-24 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => removeExistingImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload de Novas Imagens */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {formData.gallery.length > 0 ? 'Adicionar Mais Imagens' : 'Galeria do Projeto *'}
          </label>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              id="portfolio-images"
              multiple
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
            <label
              htmlFor="portfolio-images"
              className="cursor-pointer flex flex-col items-center"
            >
              <Upload className="h-12 w-12 text-gray-400 mb-2" />
              <span className="text-sm text-gray-600">
                Clique para selecionar imagens ou arraste aqui
              </span>
              <span className="text-xs text-gray-500 mt-1">
                Máximo 10 imagens (JPG, PNG, WebP, AVIF)
              </span>
            </label>
          </div>

          {/* Preview das novas imagens */}
          {imageFiles.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Novas imagens ({imageFiles.length})
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {imageFiles.map((file, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeImageFile(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={12} />
                    </button>
                    {uploadProgress[index] !== undefined && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 rounded-b-lg">
                        {Math.round(uploadProgress[index])}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <button
            type="submit"
            className="px-6 py-2 bg-[#D67441] text-white rounded-lg hover:bg-[#B85A35] transition-colors disabled:opacity-50"
            disabled={submitting || uploading}
          >
            {submitting || uploading ? 'Salvando...' : 'Adicionar Projeto'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PortfolioForm; 